import { computed, nextTick } from 'vue'
import {
  sortedAutoRtRows,
  matchesRtRowCondition,
  resolveRequestableMode,
  isAutoRtRequestable,
  buildAutoRtDialogInfo
} from '@/plugins/rt-dialog-migration'
import { getAutoRtSession } from '@/composables/useAutoRtSession'
import { useRtTextFormat } from '@/composables/useRtTextFormat'
import {
  hasPartnerTermsText,
  maskRtPartnerTermsTokens,
  unmaskRtPartnerTermsTokens
} from '@/utils/rtPartnerTermsText'
import type { CcVmLike } from '@/composables/useCareerconnectionEligibility'
import {
  isCareerconnectionAutoRtPending,
  resolveCareerconnectionAutoRtInfo
} from '@/composables/useCareerconnectionAutoRtBanner'
import * as ccHold from '@/composables/useCareerconnectionBannerHold'
import { isCareerconnectionRow } from '@/utils/realtime-transfer/careerconnectionSnapshot'
import { expectedDocumentTypeForFormat } from '@/utils/format-edit/formatDetailBackup'

type RtCondition = {
  client_id: string
  permission_type: number
  request_able: boolean
  check_flag?: unknown
}

type UnknownRecord = Record<string, any>

// 他社行の keyword / grade 等の判定は従来どおり書式 ID に 'shokumukeirekisho' を含むときだけ相手書式で見る
// （スキルシートはスキルシート自身で判定）。キャリコネ行はスキルシートも職務経歴書側として扱う
const isLegacyShokumuFormat = (formatId: string): boolean =>
  String(formatId || '').includes('shokumukeirekisho')
const isShokumukeirekiFormat = (formatId: string): boolean =>
  expectedDocumentTypeForFormat(formatId) === 'shokumukeirekisho'

type DownloadItemVm = {
  $firebase: {
    user: { uid: string; email?: string; getIdToken?: () => Promise<string> }
    isAuthenticated?: boolean
  }
  $deviceLib: { isMobileDevice: boolean }
  $site: { appFlag: string }
  $axios: {
    post: (
      url: string,
      data: FormData,
      config: { timeout: number }
    ) => Promise<any>
  }
  $varuna: { endPoints: { displayDialog: string } }
  $globalVar: UnknownRecord
  isAgree?: boolean
  formatId?: string
}

function isSameAutoRtSelection(
  current: UnknownRecord,
  selected: UnknownRecord
): boolean {
  return (
    String(current.clientId || '') === String(selected.clientId || '') &&
    Number(current.permissionType || 0) ===
      Number(selected.permissionType || 0) &&
    Number(current.oldPermissionType || 0) ===
      Number(selected.oldPermissionType || 0)
  )
}

export function useDownloadItemAutoRt(vm: DownloadItemVm) {
  const autoRt = getAutoRtSession()
  const autoRtInfo = computed(() => autoRt.info.value)
  const hasAutoRtPartnerTerms = computed(() =>
    hasPartnerTermsText(autoRtInfo.value.partner_terms_text)
  )
  const maskedAutoRtPartner = computed(() =>
    hasAutoRtPartnerTerms.value
      ? maskRtPartnerTermsTokens(
          String(autoRtInfo.value.partner_terms_text).trim()
        )
      : { masked: '', tokens: [] }
  )
  const { formattedText: formattedAutoRtPartner } = useRtTextFormat({
    text: computed(() => maskedAutoRtPartner.value.masked),
    dialogInfo: autoRtInfo,
    spMaxCharsKey: 'partner_terms_sp_max_chars',
    pcMaxCharsKey: 'partner_terms_pc_max_chars'
  })
  const autoRtPartnerTermsHtml = computed(() => {
    const info = autoRtInfo.value
    return unmaskRtPartnerTermsTokens(
      formattedAutoRtPartner.value,
      maskedAutoRtPartner.value.tokens,
      {
        termurl: info.termurl,
        privacyurl: info.privacyurl,
        handlingurl: info.handlingurl,
        occupationurl: info.occupationurl
      }
    )
  })

  const notifyDisplay = (
    formatId: string,
    clientId: string,
    permissionType: number
  ) => {
    const formData = new FormData()
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatId)
    formData.append('dialog_name', 'auto_rt')
    formData.append('client_id', clientId)
    formData.append('permission_type', String(permissionType))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('site_flag', 'r')
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('display_count_num', '1')
    vm.$axios
      .post(vm.$varuna.endPoints.displayDialog, formData, { timeout: 60000 })
      .catch((error: unknown) => {
        console.error('[useDownloadItemAutoRt] notifyDisplay failed', {
          formatId,
          clientId,
          permissionType,
          error
        })
      })
  }

  const selectAutoRT = (
    rtconditions: RtCondition[],
    basicData: UnknownRecord,
    formatId: string,
    formatData: UnknownRecord,
    otherFormatData: UnknownRecord
  ): UnknownRecord | null => {
    // ヤマト住建（is_BehindRT）向けには auto_rt を一切表示しない。
    if (vm.$globalVar.is_BehindRT && vm.$globalVar.is_BehindRT()) {
      return null
    }

    // shokumukeirekisho フォーマット時は keyword/grade/foreignStudent の判定対象を otherFormatData に切替える。
    const isMobileDevice = vm.$deviceLib.isMobileDevice === true
    const buildConditionCtx = (isShoku: boolean) => ({
      formatId,
      formatData: isShoku ? otherFormatData : formatData,
      // Hope*Valid は履歴書側にしか無い項目を見るので相手書式も渡す
      otherFormatData: isShoku ? formatData : otherFormatData,
      basicData,
      isMobileDevice,
      userEmail:
        (vm.$firebase && vm.$firebase.user && vm.$firebase.user.email) || '',
      addressCheck: (bd: unknown, prefectures: string[]) =>
        vm.$globalVar.addressCheck(bd, prefectures),
      keywordCheckRirekisho: (fd: unknown, keywords: string[]) =>
        vm.$globalVar.keywordCheckRirekisho(fd, keywords),
      keywordCheckShokumukeirekisho: (fd: unknown, keywords: string[]) =>
        vm.$globalVar.keywordCheckShokumukeirekisho(fd, keywords)
    })
    const legacyCtx = buildConditionCtx(isLegacyShokumuFormat(formatId))
    const ccCtx = buildConditionCtx(isShokumukeirekiFormat(formatId))
    const rows = sortedAutoRtRows(rtconditions)
    for (const row of rows) {
      const clientId = String(row.client_id || '')
      const isCc = isCareerconnectionRow(row)
      // キャリコネ行は行ごとに独立（連携先ごとの行）。他の行（旧 RT1.0 行など）の requestable に連動させない
      const spec = isCc
        ? {
            mode: 'NewOnly' as const,
            newPermissionType: Number(row.permission_type) || 0,
            oldPermissionType: 0
          }
        : resolveRequestableMode(row)
      // SetAutoRT:
      // - 旧 permission 行が API レスポンスに無くても表示可（行欠落 = 未制限）
      // - check_flag !== '1' なら他 permission 送客済みでも新が requestable なら表示可
      if (!isAutoRtRequestable(rtconditions, clientId, spec, row.check_flag))
        continue

      if (!matchesRtRowCondition(row, isCc ? ccCtx : legacyCtx)) continue

      if (isCc) {
        // 先方判定（①媒体取得）で出し分け。未解決・非対象 → 次の行（他社のバナーと送客を止めない）。
        // 解決すると AUTO_RT が再 emit され、eligible ならキャリコネに差し替わる
        const ccArgs = [
          rtconditions,
          basicData,
          formatId,
          formatData,
          otherFormatData
        ]
        const cc = resolveCareerconnectionAutoRtInfo(
          vm as unknown as CcVmLike,
          row,
          ccArgs
        )
        if (cc !== null) return cc
        // ①待ちで、まだバナーを出していなければ少しだけ待つ（先に他社を出すとキャリコネへ差し替えないため）
        if (
          !ccHold.isCcBannerShownThisSession() &&
          isCareerconnectionAutoRtPending(
            vm as unknown as CcVmLike,
            row,
            ccArgs
          ) &&
          ccHold.shouldHoldForPendingCc()
        ) {
          return null
        }
        continue
      }
      return buildAutoRtDialogInfo(row)
    }
    return null
  }

  /**
   * 選定結果が現セッションと同等なら clear→set しない。
   * 選定なしのときだけ clear。変わったときだけ set。
   * 送客キック後（sentUntilRefresh）は再武装しない（getDisableRtCondition 再取得後のみ）。
   */
  const evaluateAutoRT = (
    rtconditions: RtCondition[],
    basicData: UnknownRecord,
    formatId: string,
    formatData: UnknownRecord,
    otherFormatData: UnknownRecord
  ) => {
    if (autoRt.isSentUntilRefresh.value) {
      return null
    }

    const selected = selectAutoRT(
      rtconditions,
      basicData,
      formatId,
      formatData,
      otherFormatData
    )
    const current = autoRt.getAutoRtInfo()

    if (!selected) {
      if (current.clientId) {
        autoRt.clearAutoRtInfo()
      }
      return null
    }

    if (current.clientId && isSameAutoRtSelection(current, selected)) {
      ccHold.markCcBannerSessionShown()
      return selected
    }
    // この出力画面で他社を出したあとに①が解決しても差し替えない（他社への同意を持ち越さない）
    if (ccHold.keepShownOverCc(current, selected)) return current

    autoRt.setAutoRtInfo({ ...selected, agreed: true })
    ccHold.markCcBannerSessionShown()
    return selected
  }

  const handleAutoRt = (
    rtconditions: RtCondition[],
    basicData: UnknownRecord,
    formatId: string,
    formatData: UnknownRecord,
    otherFormatData: UnknownRecord
  ) => {
    evaluateAutoRT(
      rtconditions,
      basicData,
      formatId,
      formatData,
      otherFormatData
    )
    if (autoRt.getAutoRtInfo().clientId) {
      autoRt.setAgreed(!!vm.isAgree)
    }
  }

  const handleUpdateRtCondition = (
    clientId: string,
    permissionType: number | undefined | null,
    isSended: boolean,
    _dialogName: string
  ) => {
    // 未送信 close ではバナーを残す（auto_rt / 関連 RT1.0 共通）
    if (isSended !== true) {
      return
    }

    const info = autoRt.getAutoRtInfo()
    if (!info || !info.clientId) {
      return
    }

    if (String(info.clientId) !== String(clientId)) {
      return
    }

    const hasPermissionType =
      permissionType !== undefined && permissionType !== null
    const matchedPermission =
      !hasPermissionType ||
      Number(info.permissionType) === Number(permissionType) ||
      Number(info.oldPermissionType) === Number(permissionType)

    if (!matchedPermission) {
      return
    }

    // 関連 RT1.0（または同一 client の送客）成功 → auto_rt バナーを除外
    // FormatEdit 側のペア判定が同イベント内で autoRTInfo を参照するため nextTick で clear
    nextTick(() => {
      autoRt.clearAutoRtInfo()
    })
  }

  /**
   * auto_rt の CV 計測用 id の前半（`{clientId}_{permissionType}`）。非対象なら空文字。
   * RT1.0 の useGoogleAdsTag と同じく、DB に Google Ads の CV 設定
   * （google_conversation_id + google_conversation_label の両方）が入っている条件だけを対象にする
   * ＝ コード側にクライアントのホワイトリストを持たない。
   * permissionType のフォールバックは useAutoRtSend.buildConsultFromClaim と揃える。
   */
  const rtCvIdBase = computed(() => {
    if (!vm.$firebase || !vm.$firebase.isAuthenticated) return ''
    const info = autoRt.getAutoRtInfo()
    if (!info || !info.clientId) return ''
    // 同意チェックOFF時は送客されない（rtOutputButtonGate が agreed !== true で return）。
    // 正本は session 側の agreed（onRtAgreeChange → setAgreed で同期される）
    if (info.agreed !== true) return ''
    if (
      info.googleConversationId == null ||
      info.googleConversationLabel == null
    ) {
      return ''
    }
    const perm = Number.isFinite(Number(info.permissionType))
      ? Number(info.permissionType)
      : Number(info.oldPermissionType)
    if (!Number.isFinite(perm)) return ''
    return `${info.clientId}_${perm}`
  })

  /**
   * 完了画面のボタン id。auto_rt 送客が実際に発火する2経路にだけ CV 用 id を出す。
   * - pdfDownloadBtnId: 端末PDF（useParallelPdfDownload → runOutputButtonGate({ withAutoRt: true })）
   * - pdfMailBtnId: 自分にメール（FormatEdit.mailPDF → 同上）
   * コンビニ印刷・メール便は withAutoRt: false（送客しない）ので対象外。
   * メール未確認ユーザー向けの send-email-verification ボタンも送客しないため据え置き。
   * 非対象時は従来の固定 id に戻る（GTM の既存 GA4 トリガーはそのまま動く）。
   */
  const pdfDownloadBtnId = computed(() =>
    rtCvIdBase.value
      ? `pdf-dlrt-${rtCvIdBase.value}-completed`
      : 'pdf-download-btn'
  )
  const pdfMailBtnId = computed(() =>
    rtCvIdBase.value
      ? `pdfmail-dlrt-${rtCvIdBase.value}-completed`
      : 'pdf-mail-btn'
  )

  return {
    evaluateAutoRT,
    selectAutoRT,
    notifyDisplay,
    handleAutoRt,
    handleUpdateRtCondition,
    rtCvIdBase,
    pdfDownloadBtnId,
    pdfMailBtnId,
    /** DownloadItem.vue の setup() が return にスプレッドする分（テンプレート公開用） */
    templateBindings: {
      handleAutoRt,
      handleUpdateRtCondition,
      pdfDownloadBtnId,
      pdfMailBtnId
    },
    setAgreed: autoRt.setAgreed,
    clearAutoRtInfo: autoRt.clearAutoRtInfo,
    getAutoRtInfo: autoRt.getAutoRtInfo,
    isActive: () => !!autoRt.getAutoRtInfo().clientId,
    hasAutoRtPartnerTerms,
    autoRtPartnerTermsHtml
  }
}
